import React, { useState } from 'react';
import { ColumnType } from 'antd/lib/table';
import {
  DraggableProvided,
  DraggableProvidedDragHandleProps,
  DraggableProvidedDraggableProps,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

import IbButton from '../../../../../components/common/IbButton';
import IbContextMenu, { IIbContextMenuItem } from '../../../../../components/common/IbContextMenu';
import IbDivider from '../../../../../components/common/IbDivider';
import IbIcon from '../../../../../components/common/IbIcon';
import IbSwitch from '../../../../../components/common/IbSwitch';
import IbTable from '../../../../../components/common/IbTable';
import IbTooltip from '../../../../../components/common/IbTooltip';
import IbTypography from '../../../../../components/common/IbTypography';
import { IGuardrailSummaryItem, ICheckStep, isSettingsEnabled } from '../../shared/utils';

import {
  CONTEXT_CLASS_NAME,
  DISABLED_CLASS_NAME,
  DRAGGING_CLASS_NAME,
  DRAG_HANDLE_CLASS_NAME,
  DRAG_HANDLE_DISABLED_CLASS_NAME,
  DRAG_HANDLE_ICON_CLASS_NAME,
  FOOTER_CLASS_NAME,
  HEADER_CLASS_NAME,
  MAIN_CLASS_NAME,
  ORDER_CLASS_NAME,
  SUMMARY_CLASS_NAME,
  SUMMARY_ITEM_VALUE_CLASS_NAME,
  SUMMARY_TABLE_CLASS_NAME,
  SUMMARY_PROMPT_VALUE_CLASS_NAME,
  TOOLTIP_CLASS_NAME,
  TITLE_CLASS_NAME,
} from './const';

export interface ICheckListItemCommonProps {
  index: number;
  total: number;
  saving: boolean;
  draggableProps?: DraggableProvidedDraggableProps;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  innerRef?: DraggableProvided['innerRef'];
  isDragging?: boolean;
  onSetup: (stepKey: string) => void;
  onMove: (stepKey: string, direction: -1 | 1) => void;
  onDelete: (stepKey: string) => void;
  onStepChange: (stepKey: string, enabled: boolean) => void;
}

export interface IBaseCheckListItemProps extends ICheckListItemCommonProps {
  step: ICheckStep;
  title: string;
  summaryItems: IGuardrailSummaryItem[];
}

interface ISummaryTableRow {
  key: string;
}

const BaseCheckListItem: React.FC<IBaseCheckListItemProps> = ({
  step,
  title,
  summaryItems,
  index,
  total,
  saving,
  draggableProps,
  dragHandleProps,
  innerRef,
  isDragging,
  onSetup,
  onMove,
  onDelete,
  onStepChange,
}) => {
  const { t } = useTranslation();
  const [contextVisible, setContextVisible] = useState(false);
  const enabled = isSettingsEnabled(step.check.state);

  const onEnabledChange = (enabled: boolean) => onStepChange(step.key, enabled);

  const onContextMenuClose = () => {
    setContextVisible(false);
  };

  const onContextMenuOpen = () => {
    setContextVisible(true);
  };

  const menuItems = [
    {
      icon: <IbIcon iconName="setting-two" size={20} />,
      text: t('Agent: Guardrails: Steps: Setup'),
      disabled: saving,
      onSelect: () => onSetup(step.key),
    },
    {
      icon: <IbIcon iconName="up" size={20} />,
      text: t('Agent: Guardrails: Steps: Move up'),
      disabled: saving || index === 0,
      onSelect: () => onMove(step.key, -1),
    },
    {
      icon: <IbIcon iconName="down" size={20} />,
      text: t('Agent: Guardrails: Steps: Move down'),
      disabled: saving || index === total - 1,
      onSelect: () => onMove(step.key, 1),
    },
    {
      icon: <IbIcon iconName="delete" size={20} />,
      text: t('Agent: Guardrails: Steps: Delete'),
      disabled: saving,
      onSelect: () => onDelete(step.key),
    },
  ] as IIbContextMenuItem[];

  const className = [MAIN_CLASS_NAME, enabled ? '' : DISABLED_CLASS_NAME, isDragging ? DRAGGING_CLASS_NAME : '']
    .filter(Boolean)
    .join(' ');

  const renderPromptTooltip = (title: string) => {
    return <div className={TOOLTIP_CLASS_NAME}>{title}</div>;
  };

  const renderSummaryValue = (summaryItem: IGuardrailSummaryItem) => {
    if (summaryItem.kind === 'prompt') {
      const promptValue = (
        <IbTypography.Paragraph className={SUMMARY_PROMPT_VALUE_CLASS_NAME} type="secondary">
          {summaryItem.value}
        </IbTypography.Paragraph>
      );

      return summaryItem.configured ? (
        <IbTooltip title={renderPromptTooltip(summaryItem.value)}>{promptValue}</IbTooltip>
      ) : (
        promptValue
      );
    }

    return (
      <div className={SUMMARY_ITEM_VALUE_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">{summaryItem.value}</IbTypography.Paragraph>
      </div>
    );
  };

  const summaryColumns: ColumnType<ISummaryTableRow>[] = summaryItems.map((summaryItem) => ({
    title: summaryItem.label,
    render: () => renderSummaryValue(summaryItem),
    width: summaryItem.kind === 'prompt' ? undefined : '20%',
  }));

  const summaryDataSource: ISummaryTableRow[] = [{ key: step.key }];

  const dragHandleClassName = [DRAG_HANDLE_CLASS_NAME, !dragHandleProps ? DRAG_HANDLE_DISABLED_CLASS_NAME : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={innerRef} className={className} {...draggableProps}>
      <div className={HEADER_CLASS_NAME}>
        <div className={ORDER_CLASS_NAME}>{index + 1}</div>
        <div className={dragHandleClassName} {...(dragHandleProps ?? {})}>
          <IbIcon className={DRAG_HANDLE_ICON_CLASS_NAME} iconName="drag" size={16} />
        </div>
        <IbSwitch checked={enabled} disabled={saving} size="small" onChange={onEnabledChange} />
        <div className={TITLE_CLASS_NAME}>
          <IbTypography.Paragraph ellipsis strong type="secondary">
            {title}
          </IbTypography.Paragraph>
        </div>
        <IbContextMenu menuItems={menuItems} visible={contextVisible} onClose={onContextMenuClose}>
          <IbButton
            className={CONTEXT_CLASS_NAME}
            icon={<IbIcon iconName="more-one" />}
            type="icon"
            onClick={onContextMenuOpen}
          />
        </IbContextMenu>
      </div>
      <IbDivider />
      <div className={FOOTER_CLASS_NAME}>
        <div className={SUMMARY_CLASS_NAME}>
          <IbTable
            className={SUMMARY_TABLE_CLASS_NAME}
            columns={summaryColumns}
            dataSource={summaryDataSource}
            rowKey="key"
            size="small"
          />
        </div>
      </div>
    </div>
  );
};

export default BaseCheckListItem;
